import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    TextField, 
    Button, 
    CircularProgress, 
    FormControlLabel, 
    Checkbox, 
    IconButton, 
    List, 
    ListItem, 
    Select, 
    MenuItem, 
    FormControl, 
    InputLabel 
} from '@mui/material';
import {
    Add,
    Delete,
    ArrowBackIosNew,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const ACTypePage = () => {
    useAuthCheck();
    const { acTypeId } = useParams();    
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const ranks = useSelector((state) => state.crewRanks);

    const [acType, setACType] = useState({});    
    const initialValues = {
        acTypeCode: acType.acTypeCode || '',
        description: acType.description || '',
        capacity: acType.capacity || 180,
        crewComplement: acType.crewComplement || [{ rankId: '', quantity: 0, isCompulsory: false }],
        isActive: acType.isActive !== undefined ? acType.isActive : true,
    };
    

    useEffect(() => {
        if (!acTypeId) return;
        const fetchData = async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/actypes/${acTypeId}`);
                setACType(data);
            } catch (err) {
                setError(err);
                toast.error(err?.response?.data?.message || 'Failed to fetch AC Type!');
            } finally {
                setLoading(false);
            }
        };
        
        fetchData();
    }, [acTypeId]);
    
    const checkoutSchema = yup.object().shape({
        acTypeCode: yup.string().required('Required'),
        capacity: yup.number().required('Required').min(0),
        crewComplement: yup.array().of(
            yup.object().shape({
                rankId: yup.string().required('Rank ID is required').oneOf(ranks.map(rank => rank._id), 'Invalid rank'),
                quantity: yup.number().required('Quantity is required').min(0),
                isCompulsory: yup.boolean().required('Is Compulsory is required'),
            })
        )        
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = acTypeId ? 'PUT' : 'POST';
            const url = acTypeId
                ? `/actypes/update/${acTypeId}` 
                : '/actypes/new'

            await api({
                method,
                url,
                data: values,
            });

            toast.success(acTypeId 
                ? 'AC Type updated successfully.' 
                : 'AC Type created successfully.'
            );

            if (!acTypeId) {
                onSubmitProps.resetForm();
            }

            navigate("/actypes");
        } catch (error) {
            setError(error);
            toast.error(error?.response?.data?.message || "Failed to submit AC Type!");
        }
    };

    const addCrewMember = (values, setFieldValue) => {
        setFieldValue('crewComplement', [...values.crewComplement, { rankId: '', quantity: 1, isCompulsory: false }]);
    };
    
    const removeCrewMember = (values, setFieldValue, index) => {
        const newCrewRank = values.crewComplement.filter((_, i) => i !== index);
        setFieldValue('crewComplement', newCrewRank);
    };
    
    if (loading) return (
        <FlexCenter height={LAYOUT_HEIGHT}>
            <CircularProgress />
        </FlexCenter>
    );

    if (error) return (
        <FlexCenter height={LAYOUT_HEIGHT}>
            <Typography color="error">{error?.message}</Typography>
        </FlexCenter>
    );

    
    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="AC Type"
                    subtitle={`${loading ? 'Loading...' : acTypeId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/actypes")}
                        >
                            <ArrowBackIosNew />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>            
            <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                <Formik
                    initialValues={initialValues}
                    enableReinitialize={true}
                    validationSchema={checkoutSchema}
                    onSubmit={handleFormSubmit}
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleBlur,
                        handleChange,
                        handleSubmit,
                        setFieldValue,
                    }) => (
                        <form onSubmit={handleSubmit}>
                            <Box
                                display="grid"
                                gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                gap={2}
                            >
                                <TextField
                                    required
                                    fullWidth
                                    variant="filled"
                                    type="text"
                                    label="AC Type Code"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.acTypeCode}
                                    name="acTypeCode"
                                    error={!!touched.acTypeCode && !!errors.acTypeCode}
                                    helperText={touched.acTypeCode && errors.acTypeCode}
                                    sx={{ gridColumn: 'span 12' }}
                                />

                                <TextField
                                    fullWidth
                                    variant="filled"
                                    multiline
                                    rows={1}
                                    label="Description"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.description}
                                    name="description"
                                    error={!!touched.description && !!errors.description}
                                    helperText={touched.description && errors.description}
                                    sx={{ gridColumn: 'span 12' }}
                                />

                                <TextField
                                    fullWidth
                                    required
                                    variant="filled"
                                    type="number"
                                    label="Capacity"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.capacity}
                                    name="capacity"
                                    error={!!touched.capacity && !!errors.capacity}
                                    helperText={touched.capacity && errors.capacity}
                                    sx={{ gridColumn: 'span 12' }}
                                />

                                <Typography variant="h6" fontWeight="bold" sx={{ gridColumn: 'span 12' }}>
                                    Crew Complement
                                </Typography>

                                <List sx={{ gridColumn: 'span 12' }}>
                                    {values.crewComplement.map((crew, index) => (
                                        <ListItem key={index} secondaryAction={
                                            <IconButton 
                                                aria-label="delete" 
                                                edge="end" 
                                                onClick={() => removeCrewMember(values, setFieldValue, index)}
                                            >
                                                <Delete />
                                            </IconButton>
                                        }>
                                            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={1} width="100%">
                                                <FormControl fullWidth variant="filled" sx={{ minWidth: isMobile ? '20%' : '30%' }}>
                                                    <InputLabel id={`rank-label-${index}`}>Rank {index + 1}</InputLabel>
                                                    <Select
                                                        labelId={`rank-label-${index}`}
                                                        label={`Rank ID ${index + 1}`}
                                                        id={`rank-select-${index}`}
                                                        name={`crewComplement[${index}].rankId`}
                                                        value={crew.rankId}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={touched.crewComplement?.[index]?.rankId && !!errors.crewComplement?.[index]?.rankId}
                                                    >
                                                        {ranks.map((rank) => (
                                                            <MenuItem key={rank._id} value={rank._id}>
                                                                {rank.rankCode} 
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {touched.crewComplement?.[index]?.rankId && errors.crewComplement?.[index]?.rankId && (
                                                        <Typography color="error" variant="caption">
                                                            {errors.crewComplement[index].rankId}
                                                        </Typography>
                                                    )}
                                                </FormControl>

                                                <TextField
                                                    fullWidth
                                                    variant="filled"
                                                    type="number"
                                                    label={`Quantity ${index + 1}`}
                                                    name={`crewComplement[${index}].quantity`}
                                                    value={crew.quantity}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.crewComplement?.[index]?.quantity && !!errors.crewComplement?.[index]?.quantity}
                                                    helperText={touched.crewComplement?.[index]?.quantity && errors.crewComplement?.[index]?.quantity}
                                                    sx={{ minWidth: isMobile ? '20%' : '20%' }}
                                                />

                                                <TextField
                                                    fullWidth
                                                    variant="filled"
                                                    type="number"
                                                    label={`Crew Set ${index + 1}`}
                                                    name={`crewComplement[${index}].crewSet`}
                                                    value={crew.crewSet}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.crewComplement?.[index]?.crewSet && !!errors.crewComplement?.[index]?.crewSet}
                                                    helperText={touched.crewComplement?.[index]?.crewSet && errors.crewComplement?.[index]?.crewSet}
                                                    sx={{ minWidth: isMobile ? '20%' : '20%' }}
                                                />

                                                <FormControlLabel
                                                    sx={{ minWidth: isMobile ? '20%' : '20%' }}
                                                    control={
                                                        <Checkbox
                                                            checked={crew.isCompulsory} // Bind to specific crew item's isCompulsory
                                                            onChange={handleChange}
                                                            name={`crewComplement[${index}].isCompulsory`}
                                                            color="primary"
                                                        />
                                                    }
                                                    label="Is Compulsory"
                                                />
                                            </Box>
                                        </ListItem>
                                    ))}
                                    <ListItem>
                                        <Button 
                                            startIcon={<Add />}
                                            onClick={() => addCrewMember(values, setFieldValue)}
                                        >
                                            Add Crew Member
                                        </Button>
                                    </ListItem>
                                </List>

                                <FormControlLabel
                                    sx={{ gridColumn: 'span 12' }}
                                    control={
                                        <Checkbox
                                            checked={values.isActive}
                                            onChange={handleChange}
                                            name="isActive"
                                            color="primary"
                                        />
                                    }
                                    label="Is Active"
                                />
                                
                                <Button
                                    fullWidth
                                    type="submit"
                                    variant="contained"
                                    color="secondary"
                                    sx={{ gridColumn: "span 12", height: 42 }}
                                >
                                    {acTypeId ? "Update" : "Create"}
                                </Button>
                            </Box>
                        </form>
                    )}
                </Formik>
            </Box>
        </Box>
    );
};

export default ACTypePage;